import { CheckCircle, FileText, Loader2, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { humanFileSize } from '@/lib/utils/formatters';
import type { UploadSlot } from '@/store/uploadStore';

export function SlotRowContent({
  slot,
  t,
  onRemove,
  asDiv = false,
}: {
  slot: UploadSlot;
  t: (key: string) => string;
  onRemove: () => void;
  asDiv?: boolean;
}) {
  const clickable =
    (slot.hasAction === true || !!slot.metadata?.ctaUrl) &&
    slot.status === 'done';
  return (
    <>
      <div
        className={cn(
          'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md',
          slot.status === 'done' && 'bg-success/10',
          slot.status === 'error' && 'bg-destructive/10',
          slot.status === 'uploading' && 'bg-primary/10',
        )}
      >
        {slot.status === 'uploading' && (
          <Loader2 className='size-3.5 text-primary animate-spin' />
        )}
        {slot.status === 'done' && (
          <CheckCircle className='size-3.5 text-success' />
        )}
        {slot.status === 'error' && (
          <XCircle className='size-3.5 text-destructive' />
        )}
      </div>

      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-1.5 min-w-0'>
          <FileText className='size-3 text-muted-foreground shrink-0' />
          <p className='text-xs font-medium truncate'>{slot.filename}</p>
        </div>
        {slot.label !== undefined && (
          <p className='text-xs text-muted-foreground mt-0.5 truncate'>
            {slot.label}
          </p>
        )}
        <p className='text-xs text-muted-foreground mt-0.5'>
          {humanFileSize(slot.fileSize)}
        </p>

        {slot.status === 'uploading' && (
          <div className='mt-1.5 flex items-center gap-1.5'>
            <div className='flex-1 h-1 rounded-full bg-muted overflow-hidden'>
              <div
                className='h-full bg-primary rounded-full transition-all duration-200'
                style={{ width: `${slot.progress}%` }}
              />
            </div>
            <span className='text-xs tabular-nums text-muted-foreground shrink-0'>
              {slot.progress}%
            </span>
          </div>
        )}

        {slot.status === 'error' && (
          <p className='text-xs text-destructive mt-0.5 truncate'>
            {slot.error ?? t('uploads.uploadFailed')}
          </p>
        )}

        {clickable && (
          <p className='text-xs text-primary mt-0.5'>
            {t('uploads.clickToContinue')}
          </p>
        )}
      </div>

      {slot.status !== 'uploading' &&
        (asDiv ? (
          // biome-ignore lint/a11y/useSemanticElements: nested inside <button> row — span avoids invalid <button> nesting
          <span
            role='button'
            tabIndex={0}
            onClick={e => {
              e.stopPropagation();
              onRemove();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onRemove();
              }
            }}
            className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer'
          >
            <X className='size-3' />
          </span>
        ) : (
          <button
            type='button'
            onClick={e => {
              e.stopPropagation();
              onRemove();
            }}
            className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
          >
            <X className='size-3' />
          </button>
        ))}
    </>
  );
}
